import {useMemo} from 'react'
import {Platform, View} from 'react-native'
import {Image} from 'expo-image'
import {LinearGradient} from 'expo-linear-gradient'
import {
  AppBskyEmbedVideo,
  type AppBskyFeedDefs,
  AtUri,
  type ModerationDecision,
} from '@atproto/api'
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {getVideoThumbnail} from '#/lib/media/util'
import {sanitizeHandle} from '#/lib/strings/handles'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {type VideoFeedSourceContext} from '#/screens/VideoFeed/types'
import {atoms as a, useTheme} from '#/alf'
import {BLUE_HUE} from '#/alf/util/colorGeneration'
import {select} from '#/alf/util/themeSelector'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {EyeSlash_Stroke2_Corner0_Rounded as Eye} from '#/components/icons/EyeSlash'
import {Link} from '#/components/Link'
import {MediaInsetBorder} from '#/components/MediaInsetBorder'
import * as Hider from '#/components/moderation/Hider'
import {Text} from '#/components/Typography'

export const LEVEL_LABELS: Record<string, MessageDescriptor> = {
  Level_A1: msg`1st year`,
  Level_A2: msg`2nd year`,
  Level_A3: msg`3rd year`,
  Level_A4: msg`4th year`,
  Level_A5: msg`5th year`,
  Level_A6: msg`6th year`,
  Level_B1: msg`7th year`,
  Level_B2: msg`8th year`,
  Level_B3: msg`9th year`,
  Level_S1: msg`Secondary 1`,
  Level_S2: msg`Secondary 2`,
  Level_S3: msg`Secondary 3`,
  Level_S4: msg`BAC`,
}

function getBlackColor(t: ReturnType<typeof useTheme>) {
  return select(t.name, {
    light: t.palette.black,
    dark: t.atoms.bg_contrast_25.backgroundColor,
    dim: `hsl(${BLUE_HUE}, 28%, 6%)`,
  })
}

export function CapsulePostCard({
  capsulePost,
  capsule: capsuleProp,
  moderation,
}: {
  capsulePost: AppBskyFeedDefs.PostView
  capsule?: Record<string, unknown>
  sourceContext: VideoFeedSourceContext
  moderation: ModerationDecision
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const embed = capsulePost.embed
  const {
    state: pressed,
    onIn: onPressIn,
    onOut: onPressOut,
  } = useInteractionState()

  type CapsuleRecord = {
    capsule?: {
      level?: string
      subject?: string
      title?: string
      slideCount?: number
    }
  }
  const capsule =
    capsuleProp ??
    (capsulePost as unknown as CapsuleRecord).capsule ??
    (capsulePost.record as unknown as CapsuleRecord).capsule

  const questionCount =
    typeof capsule?.slideCount === 'number' ? capsule.slideCount : undefined

  const isVideoView = AppBskyEmbedVideo.isView(embed)
  const thumbnail = isVideoView ? getVideoThumbnail(embed) : undefined

  const black = getBlackColor(t)
  const rkey = useMemo(() => new AtUri(capsulePost.uri).rkey, [capsulePost.uri])

  const mergedModui = useMemo(() => {
    const modui = moderation.ui('contentList')
    const mediaModui = moderation.ui('contentMedia')
    modui.alerts = [...modui.alerts, ...mediaModui.alerts]
    modui.blurs = [...modui.blurs, ...mediaModui.blurs]
    modui.filters = [...modui.filters, ...mediaModui.filters]
    modui.informs = [...modui.informs, ...mediaModui.informs]
    return modui
  }, [moderation])

  const content = (
    <>
      <View
        style={[
          a.justify_center,
          a.rounded_md,
          a.overflow_hidden,
          {
            backgroundColor: black,
            aspectRatio: 9 / 16,
          },
          Platform.OS === 'web' && a.w_full,
        ]}>
        {thumbnail ? (
          <Image
            source={{uri: thumbnail}}
            style={[a.w_full, a.h_full, {opacity: pressed ? 0.8 : 1}]}
            accessibilityIgnoresInvertColors
            priority="high"
          />
        ) : (
          <View style={[a.w_full, a.h_full, t.atoms.bg_contrast_25]} />
        )}
        <MediaInsetBorder />

        <View style={[a.absolute, a.inset_0]}>
          {/* Coin badge — top right */}
          {questionCount != null && questionCount > 0 && (
            <View
              style={[
                a.absolute,
                {
                  top: 8,
                  right: 8,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 3,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  borderRadius: 20,
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                },
              ]}>
              <Text style={{fontSize: 11}}>🪙</Text>
              <Text
                style={[
                  a.text_xs,
                  a.font_bold,
                  {color: '#FFD700', lineHeight: 14},
                ]}>
                {questionCount}
              </Text>
            </View>
          )}

          {/* Bottom gradient with level + subject */}
          <View
            style={[
              a.absolute,
              a.inset_0,
              a.pt_2xl,
              {
                top: 'auto',
              },
            ]}>
            <LinearGradient
              colors={[black, 'rgba(0, 0, 0, 0)']}
              locations={[0.02, 1]}
              start={{x: 0, y: 1}}
              end={{x: 0, y: 0}}
              style={[a.absolute, a.inset_0, {opacity: 0.9}]}
            />

            <View style={[a.relative, a.z_10, a.p_md, a.gap_xs]}>
              <Text
                style={[a.text_sm, a.font_bold, {color: 'white'}]}
                numberOfLines={1}>
                {capsule?.level
                  ? LEVEL_LABELS[capsule.level as string]
                    ? l(LEVEL_LABELS[capsule.level as string])
                    : (capsule.level as string)
                  : undefined}
              </Text>
              <Text
                style={[a.text_xs, {color: 'rgba(255, 255, 255, 0.8)'}]}
                numberOfLines={1}>
                {capsule?.subject as string}
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={[a.pr_xs, {paddingTop: 6, gap: 4}]}>
        <Text style={[a.text_md, a.leading_snug]} numberOfLines={2} emoji>
          {capsule?.title as string}
        </Text>
        <View style={[a.flex_row, a.gap_xs, a.align_center]}>
          <View style={[a.relative, a.rounded_full, {width: 20, height: 20}]}>
            <UserAvatar
              type="user"
              size={20}
              avatar={capsulePost.author.avatar}
            />
            <MediaInsetBorder />
          </View>
          <Text
            style={[
              a.flex_1,
              a.text_sm,
              a.leading_tight,
              t.atoms.text_contrast_medium,
            ]}
            numberOfLines={1}>
            {sanitizeHandle(capsulePost.author.handle, '@')}
          </Text>
        </View>
      </View>
    </>
  )

  return (
    <Link
      accessibilityHint={l`Plays the capsule`}
      label={l`Capsule: ${(capsule?.title as string) ?? ''}`}
      to={{
        screen: 'CapsulePlay',
        params: {
          capsuleid: rkey,
          authorDid: capsulePost.author.did,
          source: 'feed',
        },
      }}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={[
        a.flex_col,
        {
          alignItems: undefined,
          justifyContent: undefined,
        },
      ]}>
      <Hider.Outer modui={mergedModui}>
        <Hider.Mask>
          <View
            style={[
              a.justify_center,
              a.rounded_md,
              a.overflow_hidden,
              {
                backgroundColor: black,
                aspectRatio: 9 / 16,
              },
              Platform.OS === 'web' && a.w_full,
            ]}>
            {thumbnail && (
              <Image
                source={{uri: thumbnail}}
                style={[a.w_full, a.h_full, {opacity: pressed ? 0.8 : 1}]}
                accessibilityIgnoresInvertColors
                blurRadius={100}
              />
            )}
            <MediaInsetBorder />
            <View
              style={[a.absolute, a.inset_0, a.justify_center, a.align_center]}>
              <View
                style={[
                  a.absolute,
                  a.inset_0,
                  a.justify_center,
                  a.align_center,
                  {
                    backgroundColor: 'black',
                    opacity: 0.2,
                  },
                ]}
              />
              <View style={[a.align_center, a.gap_xs]}>
                <Eye size="lg" fill="white" />
                <Text style={[a.text_sm, {color: 'white'}]}>{l`Hidden`}</Text>
              </View>
            </View>
          </View>
          <View style={[a.pr_xs, {paddingTop: 8, gap: 6}]}>
            <View
              style={[
                a.w_full,
                a.rounded_xs,
                t.atoms.bg_contrast_50,
                {
                  height: 14,
                },
              ]}
            />
            <View
              style={[
                a.w_full,
                a.rounded_xs,
                t.atoms.bg_contrast_50,
                {
                  height: 14,
                  width: '70%',
                },
              ]}
            />
          </View>
        </Hider.Mask>
        <Hider.Content>
          <View
            style={[
              Platform.OS === 'web' && a.w_full,
              {
                alignItems: undefined,
                justifyContent: undefined,
              },
            ]}>
            {content}
          </View>
        </Hider.Content>
      </Hider.Outer>
    </Link>
  )
}
